import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["section", "refresh", "fallback", "status", "save"];

  get saveButtons() {
    return Array.from(this.element.elements).filter((element) => element.matches('[data-mix-sources-target~="save"]'));
  }

  connect() {
    this.fallbackTargets.forEach((element) => {
      element.hidden = true;
    });
    if (this.element.dataset.previewPending) this.schedule(0);
  }

  disconnect() {
    clearTimeout(this.timer);
    this.version = (this.version || 0) + 1;
    this.request?.abort();
  }

  edited() {
    this.schedule();
  }

  changed() {
    this.schedule(0);
  }

  schedule(delay = 200) {
    if (!this.hasSectionTarget) return;
    this.element.dataset.previewPending = "true";
    clearTimeout(this.timer);
    this.request?.abort();
    this.version = (this.version || 0) + 1;
    this.saveButtons.forEach((button) => {
      button.disabled = true;
    });
    this.statusTarget.hidden = false;
    this.statusTarget.classList.remove("sr-only");
    this.statusTarget.textContent = "Updating BAC water…";
    this.sectionTarget.setAttribute("aria-busy", "true");
    this.sectionTarget.classList.add("opacity-50");
    this.timer = setTimeout(() => this.refresh(this.version), delay);
  }

  async refresh(version) {
    const request = new AbortController();
    this.request = request;
    const data = new FormData(this.element, this.refreshTarget);
    data.set(this.refreshTarget.name, this.refreshTarget.value);
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      request.abort();
    }, 10000);
    try {
      const response = await fetch(this.element.action, {
        method: "POST",
        body: data,
        headers: { Accept: "text/html" },
        signal: request.signal,
      });
      if ((!response.ok && response.status !== 422) || response.redirected) throw new Error("BAC water unavailable");
      const preview = new DOMParser().parseFromString(await response.text(), "text/html");
      const replacement = preview.getElementById(this.sectionTarget.id);
      if (!replacement) throw new Error("BAC water preview missing");
      if (version !== this.version) return;
      const focused = document.activeElement;
      const restoreFocus = this.sectionTarget.contains(focused) ? focused.id : null;
      this.sectionTarget.replaceWith(replacement);
      if (restoreFocus) document.getElementById(restoreFocus)?.focus({ preventScroll: true });
      this.statusTarget.textContent = "BAC water updated.";
      this.statusTarget.classList.add("sr-only");
      this.fallbackTargets.forEach((element) => {
        element.hidden = true;
      });
    } catch (error) {
      if ((error.name === "AbortError" && !timedOut) || version !== this.version) return;
      this.statusTarget.textContent = "The BAC water options could not update. Use Update preview to try again.";
      this.fallbackTargets.forEach((element) => {
        element.hidden = false;
      });
    } finally {
      clearTimeout(timeout);
      if (version === this.version) {
        delete this.element.dataset.previewPending;
        this.saveButtons.forEach((button) => {
          button.disabled = false;
        });
        this.sectionTarget.removeAttribute("aria-busy");
        this.sectionTarget.classList.remove("opacity-50");
      }
    }
  }
}
